/**
 * WelcomeScreen - Landing view with humanizing quotes
 *
 * Displays when no content is selected. Shows rotating historical
 * quotes about humanization with keyword highlighting.
 * Shows recent buffers so users can quickly resume previous work.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { HISTORICAL_QUOTES, type HistoricalQuote } from '../../data/historical-quotes';
import { useBufferSync, type BufferSummary } from '../../contexts/BufferSyncContext';
import { createLogger } from '../../utils/debug';

const log = createLogger('WelcomeScreen');

// ═══════════════════════════════════════════════════════════════════════════
// QUICK START GUIDE DATA
// ═══════════════════════════════════════════════════════════════════════════

const WORKFLOW_STEPS = [
  {
    num: '1',
    title: 'Browse your archive',
    description: 'Open the Archive panel (left) to browse, search, or import conversations.',
    hint: 'Click any conversation to load it into the workspace.',
  },
  {
    num: '2',
    title: 'Collect cards in the Stack',
    description: 'Switch to Stack view in the toolbar. Add passages from your archive as cards.',
    hint: 'Use "Add to Stack" in search results, or the Curator (Find in Archive) tool.',
  },
  {
    num: '3',
    title: 'Generate an Outline',
    description: 'With 2+ cards stacked, click Outline in the toolbar. Choose an arc type (or let AI pick), then click Generate Outline.',
    hint: 'Edit section titles and notes, then scroll down and click Generate Draft.',
  },
  {
    num: '4',
    title: 'Refine your Draft',
    description: 'The Draft view opens automatically after generation. Edit freely — select text for AI-assisted rewrites.',
    hint: 'Click "View with Sources" to see your draft alongside the original cards.',
  },
] as const;

const KEYBOARD_SHORTCUTS = [
  { keys: 'Cmd+E', action: 'Cycle view modes' },
  { keys: 'Cmd+/', action: 'Open AUI Assistant' },
  { keys: 'Cmd+[', action: 'Toggle Archive panel' },
  { keys: 'Cmd+]', action: 'Toggle Tools panel' },
  { keys: 'Cmd+F', action: 'Find in Archive (Curator)' },
] as const;

// Guide visibility
const GUIDE_STORAGE_KEY = 'humanizer-guide-dismissed';

// Quote timing configuration
const QUOTE_DURATION_MS = 30000; // Total time each quote is shown
const FADE_DURATION_MS = 1200;   // Fade in/out transition (matches CSS)
const BOLD_DELAY_MS = 2500;      // Delay before keyword goes bold
const UNDERLINE_DELAY_MS = 8000; // Delay before keyword gets underlined

// Keyword patterns to highlight
const KEYWORD_PATTERN = /\b(humaniz\w*|humanis\w*|human)\b/gi;

// Animation phases for keyword
type KeywordPhase = 'italic' | 'italic-bold' | 'bold-underline';

export function WelcomeScreen() {
  const { buffers, switchBuffer, importText } = useBufferSync();

  // Quick start guide visibility (persisted)
  const [showGuide, setShowGuide] = useState(() => {
    try { return localStorage.getItem(GUIDE_STORAGE_KEY) !== 'true'; }
    catch { return true; }
  });
  const handleDismissGuide = useCallback(() => {
    setShowGuide(false);
    try { localStorage.setItem(GUIDE_STORAGE_KEY, 'true'); } catch {}
  }, []);
  const handleShowGuide = useCallback(() => {
    setShowGuide(true);
    try { localStorage.removeItem(GUIDE_STORAGE_KEY); } catch {}
  }, []);

  // Quote rotation state
  const [currentQuoteIndex, setCurrentQuoteIndex] = useState(() =>
    Math.floor(Math.random() * HISTORICAL_QUOTES.length)
  );
  const [isQuoteFading, setIsQuoteFading] = useState(false);
  const [keywordPhase, setKeywordPhase] = useState<KeywordPhase>('italic');
  const currentQuote: HistoricalQuote = HISTORICAL_QUOTES[currentQuoteIndex];

  // Recent buffers (skip "workspace" placeholder, filter low-quality, limit to 5)
  // E6: Match browse panel's minimum word count threshold
  const MIN_WORD_COUNT = 10;
  const recentBuffers = useMemo(() =>
    buffers
      .filter(b => b.name !== 'workspace' && b.contentLength >= MIN_WORD_COUNT)
      .slice(0, 5),
    [buffers]
  );

  const handleBufferClick = useCallback(async (buf: BufferSummary) => {
    try {
      await switchBuffer(buf.id ?? buf.name);
    } catch (err) {
      log.error('Failed to load buffer:', err);
    }
  }, [switchBuffer]);

  // Paste text state
  const [showPaste, setShowPaste] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [pasteTitle, setPasteTitle] = useState('');
  const [isImporting, setIsImporting] = useState(false);

  const handlePasteImport = useCallback(async () => {
    const text = pasteText.trim();
    if (!text) return;
    setIsImporting(true);
    try {
      const title = pasteTitle.trim() || text.slice(0, 50).replace(/\n/g, ' ');
      await importText(text, title);
      setPasteText('');
      setPasteTitle('');
      setShowPaste(false);
    } catch (err) {
      log.error('Failed to import text:', err);
    } finally {
      setIsImporting(false);
    }
  }, [pasteText, pasteTitle, importText]);

  // Rotate quotes
  useEffect(() => {
    const interval = setInterval(() => {
      setIsQuoteFading(true);
      setTimeout(() => {
        setCurrentQuoteIndex((prev) => (prev + 1) % HISTORICAL_QUOTES.length);
        setKeywordPhase('italic'); // Reset keyword phase for new quote
        setIsQuoteFading(false);
      }, FADE_DURATION_MS);
    }, QUOTE_DURATION_MS);

    return () => clearInterval(interval);
  }, []);

  // Keyword animation phases
  useEffect(() => {
    if (isQuoteFading) return;

    // Phase 1 -> 2: italic to italic-bold
    const boldTimer = setTimeout(() => {
      setKeywordPhase('italic-bold');
    }, BOLD_DELAY_MS);

    // Phase 2 -> 3: italic-bold to bold-underline
    const underlineTimer = setTimeout(() => {
      setKeywordPhase('bold-underline');
    }, UNDERLINE_DELAY_MS);

    return () => {
      clearTimeout(boldTimer);
      clearTimeout(underlineTimer);
    };
  }, [currentQuoteIndex, isQuoteFading]);

  // Render quote text with highlighted keywords
  const renderedQuote = useMemo(() => {
    const text = currentQuote.quote;
    const parts = text.split(KEYWORD_PATTERN);
    return parts.map((part, index) => {
      if (KEYWORD_PATTERN.test(part)) {
        // Reset regex lastIndex after test
        KEYWORD_PATTERN.lastIndex = 0;
        return (
          <span
            key={index}
            className={`welcome-screen__keyword welcome-screen__keyword--${keywordPhase}`}
          >
            {part}
          </span>
        );
      }
      return part;
    });
  }, [currentQuote.quote, keywordPhase]);

  return (
    <div
      className="welcome-screen"
      role="region"
      aria-label="Welcome to Humanizer Studio"
    >
      <div className="workspace__placeholder">
        {/* Hero text */}
        <h1 className="welcome-screen__hero">
          <span className="welcome-screen__hero-line">Give Yourself Enough</span>
          <span className="welcome-screen__hero-line">Respect to</span>
          <span className="welcome-screen__hero-line welcome-screen__hero-line--emphasis">
            Humanize Yourself
          </span>
        </h1>
        <p className="workspace__tagline">A Tool for Reclaiming Your Life's Stories</p>

        {/* Historical quote rotation */}
        <div
          className={`welcome-screen__quote ${isQuoteFading ? 'welcome-screen__quote--fading' : ''}`}
          aria-live="polite"
          aria-atomic="true"
        >
          <blockquote className="welcome-screen__quote-text">
            "{renderedQuote}"
          </blockquote>
          <cite className="welcome-screen__quote-attribution">
            — {currentQuote.author}, {currentQuote.year}
          </cite>
        </div>

        <hr className="workspace__divider" aria-hidden="true" />

        {/* Paste text section */}
        <div className="welcome-screen__paste">
          {!showPaste ? (
            <button
              className="welcome-screen__paste-trigger"
              onClick={() => setShowPaste(true)}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                <path d="M3.626 3.533a.249.249 0 0 0-.126.217v9.5c0 .138.112.25.25.25h8.5a.25.25 0 0 0 .25-.25v-9.5a.249.249 0 0 0-.126-.217.248.248 0 0 0-.25.008A2.22 2.22 0 0 1 10.874 4H5.126a2.22 2.22 0 0 1-1.25-.459.248.248 0 0 0-.25-.008ZM5.126 2.5a.75.75 0 0 0 .593-1.213A.878.878 0 0 1 6.25.5h3.5a.877.877 0 0 1 .531.787.75.75 0 0 0 .593 1.213h.001a3.721 3.721 0 0 0 1.902-.533A1.749 1.749 0 0 1 14 3.75v9.5A1.75 1.75 0 0 1 12.25 15h-8.5A1.75 1.75 0 0 1 2 13.25v-9.5c0-.631.335-1.213.879-1.532A3.721 3.721 0 0 0 4.781 2.5Z"/>
              </svg>
              Paste or type text
            </button>
          ) : (
            <div className="welcome-screen__paste-form">
              <input
                className="welcome-screen__paste-title"
                type="text"
                placeholder="Title (optional)"
                value={pasteTitle}
                onChange={e => setPasteTitle(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
              />
              <textarea
                className="welcome-screen__paste-textarea"
                placeholder="Paste or type your text here..."
                value={pasteText}
                onChange={e => setPasteText(e.target.value)}
                rows={6}
                autoFocus
              />
              <div className="welcome-screen__paste-actions">
                <button
                  className="welcome-screen__paste-btn welcome-screen__paste-btn--import"
                  onClick={handlePasteImport}
                  disabled={!pasteText.trim() || isImporting}
                >
                  {isImporting ? 'Importing...' : 'Import'}
                </button>
                <button
                  className="welcome-screen__paste-btn welcome-screen__paste-btn--cancel"
                  onClick={() => { setShowPaste(false); setPasteText(''); setPasteTitle(''); }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {recentBuffers.length > 0 ? (
          <div className="welcome-screen__recent">
            <h3 className="welcome-screen__recent-title">Recent</h3>
            <div className="welcome-screen__recent-list">
              {recentBuffers.map(buf => (
                <button
                  key={buf.id ?? buf.name}
                  className="welcome-screen__recent-item"
                  onClick={() => handleBufferClick(buf)}
                  title={buf.name}
                >
                  <span className="welcome-screen__recent-name">{buf.name}</span>
                  <span className="welcome-screen__recent-meta">
                    {buf.contentLength.toLocaleString()} words
                  </span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="welcome-screen__instruction">
            <p>Access your archive through the AUI</p>
            <p className="welcome-screen__hint">
              Use semantic search, clusters, and harvesting to find content
            </p>
          </div>
        )}

        {/* Quick Start Guide trigger */}
        <button
          className="welcome-screen__guide-trigger"
          onClick={handleShowGuide}
        >
          Quick Start Guide
        </button>

        {/* Quick Start Guide modal */}
        {showGuide && (
          <div
            className="welcome-screen__guide-modal-overlay"
            onClick={handleDismissGuide}
            role="dialog"
            aria-modal="true"
            aria-label="Quick Start Guide"
          >
            <div className="welcome-screen__guide-modal" onClick={e => e.stopPropagation()} role="presentation">
              <div className="welcome-screen__guide-header">
                <h3 className="welcome-screen__guide-title">Quick Start</h3>
                <button
                  className="welcome-screen__guide-dismiss"
                  onClick={handleDismissGuide}
                  aria-label="Close guide"
                  title="Close"
                >
                  &times;
                </button>
              </div>

              <div className="welcome-screen__guide-steps">
                {WORKFLOW_STEPS.map((step) => (
                  <div key={step.num} className="welcome-screen__guide-step">
                    <span className="welcome-screen__guide-num">{step.num}</span>
                    <div className="welcome-screen__guide-content">
                      <strong className="welcome-screen__guide-step-title">{step.title}</strong>
                      <p className="welcome-screen__guide-step-desc">{step.description}</p>
                      <p className="welcome-screen__guide-step-hint">{step.hint}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="welcome-screen__guide-shortcuts">
                <h4 className="welcome-screen__guide-shortcuts-title">Keyboard Shortcuts</h4>
                <div className="welcome-screen__guide-shortcut-list">
                  {KEYBOARD_SHORTCUTS.map((sc) => (
                    <div key={sc.keys} className="welcome-screen__guide-shortcut">
                      <kbd className="welcome-screen__guide-kbd">{sc.keys}</kbd>
                      <span className="welcome-screen__guide-shortcut-desc">{sc.action}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        <p className="welcome-screen__nav-hint">
          Press Tab to access navigation
        </p>
      </div>
    </div>
  );
}
